/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import {
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateAttributeBindingMutation,
  useGetCropAttributesQuery,
  useGetCropTaxonomyTreeQuery,
  useGetTaxonomyAttributesQuery,
  useSetTaxonomyAttributeValueMutation,
} from '../../api/cropsApi';
import type {
  CropAttributeSummary,
  TaxonomyScope,
} from '../../models/Crop';
import { toastError, toastSuccess } from '../../utils/toast';
import { TaxonomySelector, buildTaxonomyOptions, getFullTaxonomyPath } from '../../components/attributes/TaxonomySelector';
import { wrapQueryResult } from '../../utils/queryHelpers';

const { Title, Paragraph, Text } = Typography;

const styles = {
  titleNoMargin: css`
    margin: 0;
  `,
  paragraphNoMargin: css`
    margin: 0;
  `,
  smallSecondaryText: css`
    font-size: 12px;
    font-weight: normal;
  `,
  selectMinWidth: css`
    min-width: 200px;
  `,
  inputWidth150: css`
    width: 150px;
  `,
  flexBetween: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
};

type AttributeScopeKey = 'familyId' | 'genusId' | 'speciesId' | 'cultivarId';

const selectionLabelMap: Record<AttributeScopeKey, string> = {
  familyId: 'Семейство',
  genusId: 'Род',
  speciesId: 'Вид',
  cultivarId: 'Сорт',
};

const scopeNameMap: Record<AttributeScopeKey, TaxonomyScope> = {
  familyId: 'family',
  genusId: 'genus',
  speciesId: 'species',
  cultivarId: 'cultivar',
};

const AdminCropAttributesPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions || []);
  const adminScopeId = useSelector(
    (state: any) => state.saves?.activeSaveId || ''
  ) as string;

  const canManageCropAttributes = useMemo(() => {
    if (!user) return false;
    if (user.role && ['superadmin', 'superuser'].includes(String(user.role).toLowerCase())) {
      return true;
    }
    if (Array.isArray(userActions)) {
      return (
        userActions.includes('crop_attributes.manage') ||
        userActions.includes('crops.attributes.manage') ||
        userActions.includes('crops.attributes.update')
      );
    }
    return false;
  }, [user, userActions]);

  const { data: taxonomy, isLoading: taxonomyTreeLoading } = useGetCropTaxonomyTreeQuery();
  const taxonomyOptions = useMemo(() => buildTaxonomyOptions(taxonomy), [taxonomy]);

  const [selectedPath, setSelectedPath] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const fullTaxonomyPath = useMemo(
    () => getFullTaxonomyPath(taxonomy, selectedPath),
    [taxonomy, selectedPath],
  );

  const selectedScope = useMemo(() => {
    if (!selectedPath.length) return null;
    const normalizedValues = selectedPath.map((value) => String(value));
    let key: AttributeScopeKey | null = null;
    let id: string | null = null;

    switch (normalizedValues.length) {
      case 1:
        key = 'familyId';
        id = normalizedValues[0];
        break;
      case 2:
        key = 'genusId';
        id = normalizedValues[1];
        break;
      case 3:
        key = 'speciesId';
        id = normalizedValues[2];
        break;
      case 4:
        key = 'cultivarId';
        id = normalizedValues[3];
        break;
    }

    if (!key || !id) return null;

    const scope = scopeNameMap[key];
    const numericScopeId = Number(id);
    
    return {
      scope,
      scopeId: Number.isNaN(numericScopeId) ? id : numericScopeId,
      label: selectionLabelMap[key],
    };
  }, [selectedPath]);

  const {
    data: systemAttributes = [],
    isFetching: catalogLoading,
    isError: catalogError,
    refetch: refetchCatalog,
  } = useGetCropAttributesQuery(
    { scopeType: 'system', saveId: adminScopeId },
    { skip: !adminScopeId }
  );

  const {
    data: taxonomyAttributes = [],
    isFetching: taxonomyAttributesLoading,
    refetch: refetchTaxonomy,
  } = useGetTaxonomyAttributesQuery(
    selectedScope && adminScopeId
      ? { scope: selectedScope.scope, scopeId: selectedScope.scopeId, saveId: adminScopeId }
      : skipToken,
  );

  const filteredTaxonomyAttributes = useMemo(() => {
    if (!search.trim()) return taxonomyAttributes;
    const normalized = search.trim().toLowerCase();
    return taxonomyAttributes.filter((item) => {
      const { attribute } = item;
      const nameMatch = attribute.name.toLowerCase().includes(normalized);
      const descriptionMatch = (attribute.description ?? '').toLowerCase().includes(normalized);
      return nameMatch || descriptionMatch;
    });
  }, [taxonomyAttributes, search]);

  const availableAttributes = useMemo(() => {
    if (!selectedScope) return [] as CropAttributeSummary[];
    const usedAttributeIds = new Set(taxonomyAttributes.map((item) => String(item.attribute.id)));
    return (systemAttributes as CropAttributeSummary[]).filter(
      (attribute) => !usedAttributeIds.has(String(attribute.id)),
    );
  }, [selectedScope, systemAttributes, taxonomyAttributes]);

  const filteredAvailableAttributes = useMemo(() => {
    if (!search.trim()) return availableAttributes;
    const normalized = search.trim().toLowerCase();
    return availableAttributes.filter((attribute) => {
      const nameMatch = attribute.name.toLowerCase().includes(normalized);
      const descriptionMatch = (attribute.description ?? '').toLowerCase().includes(normalized);
      return nameMatch || descriptionMatch;
    });
  }, [availableAttributes, search]);

  const [attributeToBind, setAttributeToBind] = useState<string>();
  const [bindingValue, setBindingValue] = useState<{ choiceId?: string | null; customValue?: string | null }>({});
  const [createAttributeBinding, { isLoading: creatingBinding }] = useCreateAttributeBindingMutation();
  const [setTaxonomyAttributeValue, { isLoading: savingValue }] = useSetTaxonomyAttributeValueMutation();

  const [editValueModalOpen, setEditValueModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<any>(null);
  const [editValueForm] = Form.useForm();

  const selectedAttributeForBinding = useMemo(() => {
    if (!attributeToBind) return null;
    return systemAttributes.find((attr) => String(attr.id) === String(attributeToBind));
  }, [attributeToBind, systemAttributes]);

  const handleOpenEditValue = (item: any) => {
    setEditingAttribute(item);
    const binding = item.binding;
    
    if (binding?.choiceId != null) {
      editValueForm.setFieldsValue({ value: { choiceId: String(binding.choiceId) } });
    } else if (binding?.customValue) {
      editValueForm.setFieldsValue({ value: { customValue: binding.customValue } });
    } else {
      editValueForm.setFieldsValue({ value: {} });
    }
    
    setEditValueModalOpen(true);
  };

  const handleSaveAttributeValue = async () => {
    if (!selectedScope || !editingAttribute) return;
    
    try {
      const values = await editValueForm.validateFields();
      const hasOptions = Array.isArray(editingAttribute.attribute.options) && editingAttribute.attribute.options.length > 0;
      
      const payload: any = {
        scope: selectedScope.scope,
        scopeId: selectedScope.scopeId,
        attributeId: editingAttribute.attribute.id,
      };
      
      if (hasOptions) {
        const choiceId = values.value?.choiceId;
        payload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
        payload.customValue = null;
      } else {
        payload.choiceId = null;
        payload.customValue = values.value?.customValue?.trim() || null;
      }
      
      await setTaxonomyAttributeValue(payload).unwrap();
      toastSuccess('Значение атрибута обновлено');
      setEditValueModalOpen(false);
      setEditingAttribute(null);
      editValueForm.resetFields();
      await wrapQueryResult(refetchTaxonomy());
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const handleAddAttributeToScope = async () => {
    if (!selectedScope || !attributeToBind) {
      toastError('Выберите атрибут для добавления');
      return;
    }

    if (!adminScopeId) {
      toastError('Не выбран активный сейв');
      return;
    }
    try {
      const numericAttributeId = Number(attributeToBind);
      const bindingPayload = {
        attributeId: Number.isNaN(numericAttributeId) ? attributeToBind : numericAttributeId,
        scope: selectedScope.scope,
        scopeId: selectedScope.scopeId,
        saveId: adminScopeId,
      };
      await createAttributeBinding(bindingPayload).unwrap();
      
      // Если указано значение по умолчанию, устанавливаем его
      const hasOptions = selectedAttributeForBinding?.options && selectedAttributeForBinding.options.length > 0;
      const hasValue = hasOptions 
        ? bindingValue.choiceId != null 
        : bindingValue.customValue != null && bindingValue.customValue.trim() !== '';
      
      if (hasValue) {
        const valuePayload: any = {
          scope: selectedScope.scope,
          scopeId: selectedScope.scopeId,
          attributeId: numericAttributeId,
          saveId: adminScopeId,
        };
        
        if (hasOptions) {
          const choiceId = bindingValue.choiceId;
          valuePayload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
          valuePayload.customValue = null;
        } else {
          valuePayload.choiceId = null;
          valuePayload.customValue = bindingValue.customValue?.trim() || null;
        }
        
        await setTaxonomyAttributeValue(valuePayload).unwrap();
      }
      
      toastSuccess('Атрибут привязан к выбранному уровню');
      setAttributeToBind(undefined);
      setBindingValue({});
      await Promise.all([refetchCatalog(), wrapQueryResult(refetchTaxonomy())]);
    } catch (error) {
      toastError(error);
    }
  };

  if (!canManageCropAttributes) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: 'Администрирование' },
              { title: 'Назначение атрибутов' },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta
            title="Недостаточно прав"
            description="Управление глобальными атрибутами культур доступно только суперпользователям."
          />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Назначение атрибутов' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <Card>
          <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
            <Title level={4} css={styles.titleNoMargin}>
              Назначение атрибутов таксономии
            </Title>
            <Paragraph type="secondary" css={styles.paragraphNoMargin}>
              Выберите уровень таксономии, чтобы просмотреть наследуемые значения и при необходимости переопределить их. Управление каталогом атрибутов доступно на странице каталога.
            </Paragraph>
            <Button type="link" onClick={() => navigate('/admin/attributes')}>
              Открыть каталог атрибутов
            </Button>
          </Space>
        </Card>

        <TaxonomySelector
          taxonomyOptions={taxonomyOptions}
          selectedPath={selectedPath}
          onPathChange={setSelectedPath}
          search={search}
          onSearchChange={setSearch}
          loading={taxonomyTreeLoading}
          disabled={false}
        />

        {selectedScope && (
          <Card
            title={
              <Space direction="vertical" size={0}>
                <Text>{selectedScope.label ?? '—'}</Text>
                {fullTaxonomyPath && (
                  <Text type="secondary" css={styles.smallSecondaryText}>
                    {fullTaxonomyPath}
                  </Text>
                )}
              </Space>
            }
            extra={
              <Space>
                <Select
                  placeholder="Привязать атрибут"
                  value={attributeToBind}
                  onChange={(value) => {
                    setAttributeToBind(value);
                    setBindingValue({});
                  }}
                  css={styles.selectMinWidth}
                  size="small"
                  showSearch
                  optionFilterProp="label"
                  options={filteredAvailableAttributes.map((attribute) => ({
                    value: String(attribute.id),
                    label: attribute.description
                      ? `${attribute.name} — ${attribute.description}`
                      : attribute.name,
                  }))}
                  disabled={filteredAvailableAttributes.length === 0 || creatingBinding}
                />
                {attributeToBind && selectedAttributeForBinding && (
                  selectedAttributeForBinding.options && selectedAttributeForBinding.options.length > 0 ? (
                    <Select
                      placeholder="Значение (необязательно)"
                      value={bindingValue.choiceId}
                      onChange={(value) => setBindingValue({ choiceId: value })}
                      css={styles.inputWidth150}
                      size="small"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      options={selectedAttributeForBinding.options.map((option: any) => ({
                        value: String(option.id),
                        label: option.code && option.code !== option.title
                          ? `${option.title} (${option.code})`
                          : option.title,
                      }))}
                      disabled={creatingBinding}
                    />
                  ) : (
                    <Input
                      placeholder="Значение (необязательно)"
                      value={bindingValue.customValue ?? ''}
                      onChange={(e) => setBindingValue({ customValue: e.target.value })}
                      css={styles.inputWidth150}
                      size="small"
                      allowClear
                      disabled={creatingBinding}
                    />
                  )
                )}
                <Button
                  size="small"
                  onClick={handleAddAttributeToScope}
                  disabled={!attributeToBind || creatingBinding}
                  loading={creatingBinding}
                >
                  Привязать
                </Button>
              </Space>
            }
          >
            {catalogLoading || taxonomyAttributesLoading ? (
              <Spin />
            ) : catalogError ? (
              <Card type="inner" size="small">
                <Text type="danger">Не удалось загрузить каталог атрибутов. Попробуйте обновить страницу.</Text>
              </Card>
            ) : filteredTaxonomyAttributes.length === 0 ? (
              <Empty description="Для выбранного уровня пока нет атрибутов" />
            ) : (
              <Descriptions bordered size="small" column={1}>
                {filteredTaxonomyAttributes.map((item) => {
                  const binding = item.binding;
                  const currentValue = binding?.choiceTitle ?? binding?.choiceValue ?? binding?.customValue ?? binding?.value ?? '—';
                  const currentCode = binding?.choiceCode;
                  const displayValue = currentCode && currentCode !== currentValue ? `${currentValue} (${currentCode})` : currentValue;
                  
                  return (
                    <Descriptions.Item
                      key={String(item.attribute.id)}
                      label={
                        <Space size={4}>
                          <span>{item.attribute.name}</span>
                          {item.attribute.scopeType === 'farm' && (
                            <Tag color="green" css={{ margin: 0 }}>Ферма</Tag>
                          )}
                        </Space>
                      }
                    >
                      <div css={styles.flexBetween}>
                        <Text>{displayValue}</Text>
                        <Button
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleOpenEditValue(item)}
                        >
                          Изменить
                        </Button>
                      </div>
                    </Descriptions.Item>
                  );
                })}
              </Descriptions>
            )}
          </Card>
        )}
      </Space>

      <Modal
        title={`Редактировать значение: ${editingAttribute?.attribute?.name ?? ''}`}
        open={editValueModalOpen}
        onOk={handleSaveAttributeValue}
        onCancel={() => {
          setEditValueModalOpen(false);
          setEditingAttribute(null);
          editValueForm.resetFields();
        }}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingValue}
      >
        <Form form={editValueForm} layout="vertical">
          {editingAttribute && Array.isArray(editingAttribute.attribute.options) && editingAttribute.attribute.options.length > 0 ? (
            <Form.Item
              label="Значение"
              name={['value', 'choiceId']}
            >
              <Select
                placeholder="Выберите значение"
                allowClear
                optionFilterProp="label"
                options={editingAttribute.attribute.options.map((option: any) => ({
                  value: String(option.id),
                  label: option.code && option.code !== option.title
                    ? `${option.title} (${option.code})`
                    : option.title,
                }))}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label="Значение"
              name={['value', 'customValue']}
            >
              <Input placeholder="Введите значение" allowClear />
            </Form.Item>
          )}
          {editingAttribute?.attribute?.description && (
            <Typography.Text type="secondary" css={commonStyles.smallText}>
              {editingAttribute.attribute.description}
            </Typography.Text>
          )}
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AdminCropAttributesPage;
